/**
 * El rollo de la térmica: cuántas etiquetas le quedan y las que no entraron.
 *
 * Una tirada que no entra en lo que queda del rollo no se corta en el aire: se
 * imprimen las que entran y el resto queda **pendiente**, guardado en este
 * navegador —el de la computadora de la impresora—, hasta que se cambie el
 * rollo y alguien apriete «Imprimir pendientes». Va en `localStorage` y no en
 * la sesión: cambiar el rollo puede llevar un rato, y cerrar la pestaña en el
 * medio no tiene que perder etiquetas.
 *
 * Cuántas quedan se sabe acá sin preguntarle al servidor porque la ventana de
 * impresión se tiene que abrir en el mismo clic: esperar una respuesta antes
 * hace que el navegador la bloquee como ventana emergente.
 */

/** La medida del rollo de la térmica, el que lleva contador. */
export const ROLLO = { ancho: 100, alto: 150 } as const;

export const esDelRollo = (m: { ancho: number; alto: number }) => m.ancho === ROLLO.ancho && m.alto === ROLLO.alto;

/** Una tirada que quedó a medias: su nombre y los dibujos que faltan. */
export type Pendiente = { nombre: string; dibujos: string[] };

const CLAVE = 'wms:rollo-pendientes';

let quedan: number | null = null;
let pendientes: Pendiente[] = leer();
let foto: { quedan: number | null; pendientes: Pendiente[] } = { quedan, pendientes };
const oyentes = new Set<() => void>();

function deposito(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

function leer(): Pendiente[] {
  try {
    const v: unknown = JSON.parse(deposito()?.getItem(CLAVE) ?? '[]');
    return Array.isArray(v) ? (v as Pendiente[]).filter((p) => Array.isArray(p?.dibujos) && p.dibujos.length > 0) : [];
  } catch {
    return [];
  }
}

function avisar() {
  foto = { quedan, pendientes };
  try {
    if (pendientes.length > 0) deposito()?.setItem(CLAVE, JSON.stringify(pendientes));
    else deposito()?.removeItem(CLAVE);
  } catch {
    /* sin lugar o modo privado: quedan en memoria mientras dure la pestaña */
  }
  for (const f of oyentes) f();
}

/** Lo que cuenta el servidor, cada vez que se entera. null, sin contador. */
export function anotarRollo(n: number | null) {
  if (n === quedan) return;
  quedan = n;
  avisar();
}

/**
 * De una tirada de `n`, cuántas entran en el rollo. Sin contador, todas: no
 * se frena una impresión por un número que nadie cargó.
 */
export const cuantasEntran = (n: number, enRollo: number | null = quedan): number =>
  enRollo === null ? n : Math.max(0, Math.min(n, enRollo));

/** Lo que salió recién: se descuenta ya, sin esperar al servidor, para la tirada siguiente. */
export function descontar(n: number) {
  if (quedan === null) return;
  quedan = Math.max(0, quedan - n);
  avisar();
}

export function guardarPendiente(p: Pendiente) {
  pendientes = [...pendientes, p];
  avisar();
}

/** Saca todas las pendientes para imprimirlas; las que tampoco entren vuelven solas. */
export function sacarPendientes(): Pendiente[] {
  const todas = pendientes;
  pendientes = [];
  avisar();
  return todas;
}

export function suscribir(f: () => void) {
  oyentes.add(f);
  return () => oyentes.delete(f);
}

export const estadoRollo = () => foto;
